// Mock data for Yuan Liu's portfolio

export const personalInfo = {
  name: "Yuan Liu",
  title: "Full-Stack Software Engineer",
  tagline: "Ship reliable products, end to end.",
  location: "NYC",
  remote: "Remote-friendly",
  github: "https://github.com/yuan-cloud",
  linkedIn: "https://linkedin.com/in/yuanliu11",
  linktree: "https://linktr.ee/eucapop",
  filmReel: "https://vimeo.com/531922143",
  bio: "I build production systems with clean architecture, strong test coverage, and measurable impact. Seven years shipping full-stack applications, from AI-powered VS Code extensions to real-time visual engines and AI reliability infrastructure. I operate self-hosted multi-agent development environments daily, orchestrating Claude Code, Codex CLI, and more with bead-graph task tracking. I turn ambiguous specs into dependable products, owned end to end.",
  funFact: "When not coding, I direct science films and build interactive art installations.",
  yearsOfExperience: "7+"
};

export const skills = {
  languages: ["TypeScript", "JavaScript (ES6+)", "Python", "SQL"],
  frontend: ["React", "React Native", "Vite", "Redux", "Tailwind CSS", "Next.js"],
  backend: ["Node.js", "Express", "REST APIs", "WebSockets"],
  databases: ["PostgreSQL", "MongoDB", "Pinecone"],
  devops: ["Docker", "AWS", "GitHub Actions", "CI/CD"],
  graphics: ["WebGL", "D3.js", "Resolume", "TouchDesigner"],
  other: ["Jest", "Zod", "DMX", "3D mo-cap", "projection-mapping"]
};

export const projects = [
  {
    id: "helios",
    title: "Architected privacy-first code visualization platform",
    description: "Designed full-stack 3D codebase explorer with AST parsing, semantic embeddings, and WebGPU acceleration—client-side only architecture.",
    outcome: "Zero backend dependencies with 100% privacy; WASM-powered Tree-sitter parsing serving 60fps force-directed graphs in browser.",
    tags: ["Full-Stack", "WebAssembly", "WebGPU", "Tree-sitter", "Three.js", "OPFS"],
    link: "https://helios-dhp.pages.dev/",
    github: "https://github.com/yuan-cloud/helios",
    featured: true,
    year: "2024",
    category: "Platform Engineering",
    metrics: "0 server calls • Client-side ML • AST parsing"
  },
  {
    id: "kura-kura",
    title: "Built multi-sensory repository visualization product",
    description: "Owned full-stack development: procedural art generation, adaptive audio synthesis, FastAPI backend with MongoDB caching.",
    outcome: "Shipped production app with 60fps Canvas rendering and 3-layer Tone.js synthesis; deterministic generation across 1000 variants.",
    tags: ["Full-Stack", "React", "Canvas API", "Tone.js", "FastAPI", "MongoDB"],
    link: "https://kura-kura-production-78d0.up.railway.app/",
    github: "https://github.com/yuan-cloud/Kura-Kura",
    featured: true,
    year: "2024",
    category: "Full-Stack Product",
    metrics: "60fps • Web Audio • Production deployed"
  },
  {
    id: "ithi-vscode",
    title: "Shipped AI-powered VS Code extension at scale",
    description: "Built full-stack extension with React webview, TypeScript backend, and custom AST parsing for 160+ language translation.",
    outcome: "Zero production incidents across global developer teams; custom AST masking algorithm maintains identifier integrity.",
    tags: ["Full-Stack", "TypeScript", "React", "VS Code", "Marketplace", "Team", "AI"],
    link: "https://marketplace.visualstudio.com/items?itemName=IthiTranslator.ithi",
    github: "https://github.com/Team-Ithi/Ithi",
    featured: true,
    year: "2024",
    category: "Developer Experience",
    metrics: "160+ languages • 0 breaking changes • Marketplace"
  },
  {
    id: "warp-records-mv",
    title: "Directed real-time generative music video for Warp Records",
    description: "Directed, composed VFX, and edited music video using TouchDesigner and JavaScript-driven procedural systems for live audio-reactive visuals. Deployed as Spotify Canvas for real-time playback.",
    outcome: "Delivered production-grade music video for major electronic label and Spotify Canvas integration; synchronized real-time generative graphics with audio analysis at 60fps throughout runtime.",
    tags: ["TouchDesigner", "JavaScript", "VFX", "Audio-Reactive", "Spotify Canvas"],
    link: "https://www.youtube.com/watch?v=CC4GwypexyM",
    featured: true,
    year: "2024",
    category: "Creative Technology",
    metrics: "Warp Records • Spotify Canvas • 60fps audio-reactive"
  },
  {
    id: "resofleur",
    title: "Optimized real-time rendering engine for production",
    description: "Re-architected visual pipeline with GPU/CPU fallbacks ensuring continuous uptime in live installations for Fortune 500 clients.",
    outcome: "Reduced render latency 35% via shader optimization; powered high-visibility productions for Nike, Amazon, Peloton.",
    tags: ["Node.js", "WebGL", "Performance", "Production"],
    link: "https://courageous-vitality-production.up.railway.app",
    github: "https://github.com/yuan-cloud/resofleur",
    featured: false,
    year: "2018-2024",
    category: "Performance Engineering",
    metrics: "−35% latency • Fortune 500 • 24/7 uptime"
  },
  {
    id: "zodia",
    title: "Designed RAG-powered content platform",
    description: "Architected full-stack application with retrieval-augmented AI for contextually accurate responses across web and mobile.",
    outcome: "Optimized vector search achieving sub-second query times; seamless cross-platform UX with unified component library.",
    tags: ["Full-Stack", "React", "TypeScript", "Node.js", "Pinecone", "MongoDB"],
    link: "https://github.com/Taz-AI-Project/Zodia",
    featured: false,
    year: "2024",
    category: "Full-Stack Product",
    metrics: "<1s queries • RAG architecture • Cross-platform"
  },
  {
    id: "nlp-pipeline",
    title: "Built cross-cultural insights engine for strategy",
    description: "Architected bilingual NLP pipeline integrating Brandwatch, Weibo, Zhihu APIs for executive decision-making.",
    outcome: "Delivered 5-8 strategic opportunity spaces from ambiguous requirements; accelerated research-to-insight timeline.",
    tags: ["Python", "NLP", "APIs", "Data Engineering"],
    link: "https://www.afterthis.co/",
    featured: false,
    year: "2025",
    category: "Data & Analytics",
    metrics: "5-8 insights • Bilingual • Executive-ready"
  },
  {
    id: "labubu",
    title: "Built React Native mobile application",
    description: "Developed cross-platform mobile app showcasing React Native best practices with native integrations and smooth UX.",
    outcome: "Production-ready mobile application demonstrating proficiency in React Native, native modules, and mobile-first design patterns.",
    tags: ["React Native", "Mobile", "TypeScript", "Cross-platform"],
    link: "https://github.com/Labubu-Who/Labubu",
    featured: false,
    year: "2024",
    category: "Mobile Development",
    metrics: "iOS & Android • Native integrations • Mobile UX"
  },
  {
    id: "notorious-big-o",
    title: "Built algorithm education platform",
    description: "Architected full-stack React visualizer teaching algorithmic complexity through real-time profiling and visual feedback.",
    outcome: "Adopted by OSLABS for developer onboarding; accelerated team ramp-up on performance optimization.",
    tags: ["React", "D3.js", "Algorithms", "Education"],
    link: "https://github.com/Notorious-Big-O",
    featured: false,
    year: "2024",
    category: "Developer Experience",
    metrics: "Open source • Team adoption • OSLABS"
  },
  {
    id: "dad-a-base",
    title: "Shipped full-stack web application",
    description: "Built end-to-end application with modern web technologies, demonstrating proficiency in full-stack development workflows.",
    outcome: "Production-deployed application showcasing clean architecture, API design, and responsive frontend implementation.",
    tags: ["React", "Node.js", "Full-Stack", "Web"],
    link: "https://github.com/Dad-a-base",
    featured: false,
    year: "2024",
    category: "Full-Stack Product",
    metrics: "Production deployed • Full-stack • Modern architecture"
  },
  {
    id: "interactive-lights",
    title: "Delivered large-scale interactive installation",
    description: "Engineered system controlling 1M+ addressable lights with music synchronization across 8 parade units.",
    outcome: "On-time delivery for Miziker Entertainment with zero downtime; flawless performance under high-visibility conditions.",
    tags: ["Hardware", "DMX", "IoT", "Real-time"],
    link: "https://blooloop.com/theme-park/news/miziker-partners-chimelong-paradise-create-turnkey-enhanced-halloween-experience/",
    featured: false,
    year: "2017",
    category: "Systems Engineering",
    metrics: "1M+ lights • 0 downtime • Live production"
  },
  {
    id: "denial-review-workbench",
    title: "Shipped HTTPS-deployed claim denial review pipeline",
    description: "Built an auditable human-in-the-loop denial review workflow for document-heavy healthcare ops, with structured AI review, reviewer approval or escalation, append-only JSONL logs, deterministic replay without model calls, and Vifei export.",
    outcome: "Live HTTPS demo with replay-verified approved runs, API-enforced escalation guards, and share-safe Vifei export from normalized workbench events.",
    tags: ["Full-Stack", "FastAPI", "React", "Anthropic", "Ansible", "Caddy"],
    link: "https://workbench.yuanliu.dev",
    github: "https://github.com/yuan-cloud/denial-review-workbench",
    featured: true,
    year: "2026",
    category: "Full-Stack Product",
    metrics: "Live HTTPS • Replay invariant • Vifei export"
  },
  {
    id: "vifei-suite",
    title: "Built deterministic replay engine for AI agents",
    description: "Built a Rust local-first evidence suite for AI agent workflows, using an append-only EventLog, canonical commit_index ordering, deterministic replay checks, Truth HUD surfaces, and share-safe export that fails closed with refusal reports.",
    outcome: "Public CLI/TUI proof system with rerunnable hashes, incident packs, refusal diagnostics, and deterministic local artifacts for run verification.",
    tags: ["Rust", "CLI", "TUI", "EventLog", "Replay"],
    link: "https://github.com/yuan-cloud/vifei-suite-public",
    github: "https://github.com/yuan-cloud/vifei-suite-public",
    featured: true,
    year: "2026",
    category: "Systems Engineering",
    metrics: "EventLog • Deterministic hashes • Fail-closed export"
  },
  {
    id: "agent-cassette",
    title: "Engineered record/replay capture layer for agent sessions",
    description: "TypeScript engine that captures AI agent runs (tool calls, model responses, side-effect gates) into deterministic cassettes for offline replay. Pairs with Vifei as the recording layer beneath the evidence engine.",
    outcome: "Open-sourced capture half of a two-stage replay pipeline; cassettes reload identically without hitting live model APIs.",
    tags: ["TypeScript", "Record/Replay", "Agents", "Capture", "CI"],
    link: "https://github.com/yuan-cloud/agent-cassette",
    github: "https://github.com/yuan-cloud/agent-cassette",
    featured: true,
    year: "2026",
    category: "Systems Engineering",
    metrics: "Capture layer • Offline replay • Pairs with Vifei"
  },
  {
    id: "flow-profile",
    title: "Built auditable context bundle generator for AI assistants and support",
    description: "Built a TypeScript and Bun monorepo with CLI workflows and a Vue 3 web app for generating deterministic, redacted context bundles from flow-based automation exports. The pipeline performs secret redaction, SARIF risk detection, token budgeting, capability manifests, and support-bundle generation for AI assistants and support workflows.",
    outcome: "Produces repeatable artifacts such as prompt-context.md and support-bundle.zip that are safe to share with support, safe to feed LLMs, and safe to commit after review.",
    tags: ["Full-Stack", "TypeScript", "Bun", "Vue 3", "SARIF"],
    link: "https://github.com/yuan-cloud/flow-profile",
    github: "https://github.com/yuan-cloud/flow-profile",
    featured: true,
    year: "2026",
    category: "Developer Experience",
    metrics: "Redaction-first • SARIF risk detection • AI-ready bundles"
  },
  {
    id: "aliveca",
    title: "Built offline-first LED Mapping Truth Pack Compiler",
    description: "Built a local-first full-stack PWA and deterministic compiler for arena-scale LED mapping workflows, turning opaque vendor pixel-map exports into validated Truth Pack ZIPs with geometry validation, QR test patterns, verification reports, viewer.html handoff, and cross-platform exports.",
    outcome: "Open-sourced local-first compiler that produces validated Truth Pack ZIPs from vendor pixel-map exports, with QR-fiducial test patterns and a self-contained viewer for offline review.",
    tags: ["Full-Stack", "TypeScript", "Bun", "PWA", "Truth Packs"],
    link: "https://github.com/yuan-cloud/aliveca",
    github: "https://github.com/yuan-cloud/aliveca",
    featured: true,
    year: "2026",
    category: "Performance Engineering",
    metrics: "Full-stack PWA • Truth Packs • Arena-scale"
  },
  {
    id: "mission-relay",
    title: "Built full-stack mobile creator and community platform",
    description: "Built a full-stack mobile creator and community platform where fans complete missions, earn engagement points, appear on a community wall, and unlock exclusive live circles, with an Expo React Native frontend, FastAPI backend, MongoDB persistence, passwordless OTP auth, JWT with refresh-token rotation, and role-based creator/fan flows.",
    outcome: "Open-source full-stack mobile platform with passwordless OTP, role-based creator/fan flows, and a creator review pipeline for fan submissions.",
    tags: ["Full-Stack", "Expo", "React Native", "FastAPI", "MongoDB", "Mobile", "Auth"],
    link: "https://github.com/yuan-cloud/MissionRelay",
    github: "https://github.com/yuan-cloud/MissionRelay",
    featured: true,
    year: "2026",
    category: "Mobile Development",
    metrics: "Expo + FastAPI • OTP + JWT rotation • Creator/Fan roles"
  }
];

export const experience = [
  {
    company: "Ithi",
    role: "Platform Engineer",
    period: "2024 – Present",
    highlights: [
      "Shipped AI-powered VS Code extension (TypeScript + React) with 160+ language support",
      "Created open source developer tools at OSLABS for algorithm education",
      "Built Zodia full-stack app with retrieval-augmented AI responses"
    ]
  },
  {
    company: "After This",
    role: "Software Engineer & Research Consultant",
    period: "2025",
    highlights: [
      "Built bilingual NLP pipelines integrating external APIs for cross-cultural insights",
      "Delivered executive-ready analytics and dashboards under tight timelines",
      "Practiced privacy-minded design for sensitive user data workflows"
    ]
  },
  {
    company: "Nitemind",
    role: "Technical Producer & Software Engineer",
    period: "2018 – 2024",
    highlights: [
      "Engineered Node.js + React control systems for real-time environments",
      "Reduced render latency by ~35% via shader and memory optimizations",
      "Delivered installations for Amazon, Nike, Peloton, Rihanna",
      "Standardized pipelines cutting production effort by ~50%"
    ]
  }
];

export const education = {
  degree: "B.S., Molecular Biology & Biochemistry",
  institution: "Reed College",
  certifications: ["Integrating Generative AI into the Creative Process"]
};

export const testimonials = [
  {
    quote: "Yuan's ability to bridge technical precision with creative vision is exceptional. The real-time systems delivered exceeded all expectations.",
    author: "Technical Director",
    company: "Nitemind"
  },
  {
    quote: "The NLP pipeline Yuan built transformed ambiguous research questions into actionable insights with remarkable clarity and speed.",
    author: "Research Lead",
    company: "After This"
  }
];
